import { blurActiveElement } from "./blurActiveElement";

// 프레임 복원(키보드 하강)에 따른 innerHeight 증가가 "멎었다"고 볼 무동작 구간.
// 애니메이션 프레임 간격(~16ms)보다 충분히 크고, 사용자 체감 지연은 작게 (실기기 QA 로 튜닝 가능).
const SETTLE_MS = 120;
// no-resize 환경(데스크톱·하드웨어 키보드·Android adjustPan/오버레이 키보드) 안전망.
// 키보드 하강(~250ms)+settle(120ms) 최악 경로를 넘겨 resize 경로를 선점(조기 오픈)하지 않도록 넉넉히 잡는다.
const FALLBACK_MS = 600;

/**
 * 입력 필드에 포커스(소프트 키보드)가 남은 상태에서 BottomSheet·키패드 등 하단 오버레이를
 * 열 때, 키보드가 다 내려가기 전에 오버레이가 먼저 올라와 겹치는 현상을 막는다.
 *
 * 인앱 WebView 는 native 가 키보드 해제 시 프레임을 키보드 높이만큼 되돌리며, 그 과정에서
 * window.innerHeight 가 커진다(iOS keyboardLayoutGuide / AOS ime inset — useAppHeight 참조).
 * 복원이 애니메이션으로 점진적일 수 있어(뷰포트 성장 애니메이션) "첫 증가"에 바로 열면 아직
 * 키보드가 내려가는 중일 수 있다. 그래서 innerHeight 증가가 SETTLE_MS 동안 멎을 때까지 기다린다.
 *
 * - 편집 요소 포커스가 없으면(=blur 대상 없음) 즉시 연다.
 * - resize 가 오지 않는 환경은 FALLBACK_MS 타임아웃으로 반드시 연다(편집 요소 포커스 시
 *   소프트 키보드가 없어도 이 만큼 지연 — no-resize 를 커버하기 위한 의도된 fallback 비용).
 *   (단일 innerHeight 신호가 모든 Android 설정을 커버한다고 보지 않는다 — 그런 환경에선 이 fallback 이 실제 오픈 경로다.)
 *
 * @returns 대기를 취소하는 함수(리스너/타이머 정리, open 미호출).
 */
export function openAfterKeyboardDismiss(open: () => void): () => void {
  const startHeight = window.innerHeight;

  // blurActiveElement: 편집 요소가 활성일 때만 blur 하고 그 사실을 반환.
  // false = blur 대상 없음(편집 포커스 없음) → 대기 없이 즉시 오픈.
  if (!blurActiveElement()) {
    open();
    return () => {};
  }

  let settled = false;
  let settleTimer: number | undefined;

  const onResize = () => {
    if (window.innerHeight <= startHeight) return;
    // 프레임이 커지는 중(키보드 하강). 증가가 멎으면(SETTLE_MS 무동작) 연다.
    window.clearTimeout(settleTimer);
    settleTimer = window.setTimeout(finish, SETTLE_MS);
  };

  // 안전망: resize 가 안 와도 반드시 오픈 (finish 는 hoisted).
  const fallbackTimer = window.setTimeout(finish, FALLBACK_MS);

  const cleanup = () => {
    window.removeEventListener("resize", onResize);
    window.clearTimeout(settleTimer);
    window.clearTimeout(fallbackTimer);
  };
  function finish() {
    if (settled) return;
    settled = true;
    cleanup();
    open();
  }
  const cancel = () => {
    if (settled) return;
    settled = true;
    cleanup();
  };

  window.addEventListener("resize", onResize);
  // blur() 가 리스너 등록 전에 동기적으로 프레임을 이미 키웠을 수 있다 → 등록 직후 1회
  // 즉시 확인해 유실된 resize 를 회수한다(아직 안 커졌으면 no-op, 커졌으면 settle 무장).
  onResize();
  return cancel;
}
